import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { Upload, X, Plus, Trash2 } from 'lucide-react';
import { productsApi } from '../../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAddProduct = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    old_price: '',
    discount: '',
    images: ['', ''], // Başlangıçta 2 görsel
    category: '',
    description: '',
    stock: 100,
    featured: false,
    bestseller: false,
    new: false,
    weekly_deal: false,
    technical_specs: [] // Teknik özellikler
  });

  // Fetch categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/categories`);
      const data = await response.json();
      
      // Flatten nested categories
      const flattenCategories = (cats, level = 0) => {
        let flat = [];
        cats.forEach(cat => {
          flat.push({
            value: cat.slug || cat.name.toLowerCase().replace(/\s+/g, '-'),
            label: '  '.repeat(level) + cat.name,
            level: level
          });
          if (cat.subcategories && cat.subcategories.length > 0) {
            flat = flat.concat(flattenCategories(cat.subcategories, level + 1));
          }
        });
        return flat;
      };
      
      const allCategories = flattenCategories(data);
      setCategories(allCategories);
    } catch (error) {
      console.error('Kategori yükleme hatası:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Filter out empty image URLs
      const validImages = formData.images.filter(img => img.trim() !== '');
      
      if (validImages.length === 0) {
        alert('En az 1 ürün görseli eklemelisiniz!');
        setLoading(false);
        return;
      }

      // Calculate discount if old_price is provided
      let productData = { 
        ...formData,
        image: validImages[0], // Ana görsel
        image2: validImages[1] || null // İkinci görsel (varsa)
      };
      
      // Remove images array, we use image and image2
      delete productData.images;

      // Filter out empty technical specs
      const validSpecs = formData.technical_specs.filter(
        spec => spec.key.trim() !== '' && spec.value.trim() !== ''
      );
      productData.technical_specs = validSpecs.length > 0 ? validSpecs : null;

      if (formData.old_price && formData.price) {
        const discount = Math.round(((formData.old_price - formData.price) / formData.old_price) * 100);
        productData.discount = discount;
      }

      // Convert string numbers to actual numbers
      productData.price = parseFloat(productData.price);
      productData.old_price = formData.old_price ? parseFloat(formData.old_price) : null;
      productData.stock = parseInt(productData.stock);

      await productsApi.create(productData);
      alert('Ürün başarıyla eklendi!');
      navigate('/admin/products');
    } catch (error) {
      console.error('Error creating product:', error);
      alert('Ürün eklenirken hata oluştu!');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Image management functions
  const addImageField = () => {
    setFormData({
      ...formData,
      images: [...formData.images, '']
    });
  };

  const removeImageField = (index) => {
    if (formData.images.length <= 1) {
      alert('En az 1 görsel alanı olmalıdır!');
      return;
    }
    setFormData({
      ...formData,
      images: formData.images.filter((_, i) => i !== index)
    });
  };

  const updateImageUrl = (index, value) => {
    const updated = [...formData.images];
    updated[index] = value;
    setFormData({ ...formData, images: updated });
  };

  const handleImageUpload = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Görsel boyutu 5MB\'dan küçük olmalıdır!');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        updateImageUrl(index, reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Technical Specs management
  const addSpecField = () => {
    setFormData({
      ...formData,
      technical_specs: [...formData.technical_specs, { key: '', value: '' }]
    });
  };

  const removeSpecField = (index) => {
    setFormData({
      ...formData,
      technical_specs: formData.technical_specs.filter((_, i) => i !== index)
    });
  };

  const updateSpecField = (index, field, value) => {
    const updated = [...formData.technical_specs];
    updated[index][field] = value;
    setFormData({ ...formData, technical_specs: updated });
  };

  return (
    <AdminLayout>
      <div>
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800">Yeni Ürün Ekle</h1>
          <p className="text-gray-600 mt-2">Mağazanıza yeni ürün ekleyin</p>
        </div>

        <form onSubmit={handleSubmit} className="max-w-4xl">
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold mb-4">Temel Bilgiler</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Ürün Adı *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                  placeholder="Örn: Cep Premium Air 105 Bluetooth Kulaklık"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Açıklama *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                  placeholder="Ürün açıklaması..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Kategori *</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                  >
                    <option value="">Kategori Seçin</option>
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Stok Adedi *</label>
                  <input
                    type="number"
                    name="stock"
                    value={formData.stock}
                    onChange={handleChange}
                    required
                    min="0"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold mb-4">Fiyat Bilgileri</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Satış Fiyatı (TL) *</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  required
                  step="0.01"
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                  placeholder="2999"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Eski Fiyat (TL) - İndirim için</label>
                <input
                  type="number"
                  name="old_price"
                  value={formData.old_price}
                  onChange={handleChange}
                  step="0.01"
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                  placeholder="3999"
                />
                <p className="text-xs text-gray-500 mt-1">İndirim yüzdesi otomatik hesaplanır</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold">Ürün Görselleri</h2>
                <p className="text-sm text-gray-600 mt-1">URL veya dosya yükleyerek görsel ekleyin</p>
              </div>
              <button
                type="button"
                onClick={addImageField}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-semibold"
              >
                <Plus size={16} />
                Yeni Görsel Ekle
              </button>
            </div>
            
            <div className="space-y-4">
              {formData.images.map((image, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium">
                      Görsel {index + 1} {index === 0 && <span className="text-red-600">*</span>}
                    </label>
                    {formData.images.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeImageField(index)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>

                  {/* URL Input */}
                  <div className="mb-3">
                    <label className="block text-xs text-gray-600 mb-1">Görsel URL</label>
                    <input
                      type="url"
                      value={image}
                      onChange={(e) => updateImageUrl(index, e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>

                  {/* File Upload */}
                  <div className="mb-3">
                    <label className="block text-xs text-gray-600 mb-1">veya Dosya Yükle</label>
                    <div className="flex items-center gap-3">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(index, e)}
                        className="hidden"
                        id={`image-upload-${index}`}
                      />
                      <label
                        htmlFor={`image-upload-${index}`}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700 transition-colors text-sm font-medium"
                      >
                        <Upload size={16} />
                        Görsel Yükle
                      </label>
                      <span className="text-xs text-gray-500">Max 5MB</span>
                    </div>
                  </div>

                  {/* Image Preview */}
                  {image && (
                    <div className="mt-3">
                      <label className="block text-xs text-gray-600 mb-1">Önizleme</label>
                      <div className="relative inline-block">
                        <img 
                          src={image} 
                          alt={`Önizleme ${index + 1}`} 
                          className="w-32 h-32 object-cover rounded border border-gray-300" 
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/128?text=Hatalı+Görsel';
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => updateImageUrl(index, '')}
                          className="absolute -top-2 -right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            <p className="text-xs text-gray-500 mt-3">
              💡 İpucu: İlk görsel ana görsel olarak kullanılır. Daha fazla görsel eklemek için "Yeni Görsel Ekle" butonunu kullanın.
            </p>
          </div>

          {/* Technical Specifications */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">⚙️ Teknik Özellikler</h2>
              <button
                type="button"
                onClick={addSpecField}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                <Plus size={16} />
                Özellik Ekle
              </button>
            </div>

            {formData.technical_specs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>Henüz teknik özellik eklenmedi.</p>
                <p className="text-sm mt-2">Ürüne teknik özellik eklemek için yukarıdaki butonu kullanın.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {formData.technical_specs.map((spec, index) => (
                  <div key={index} className="flex gap-3 items-start">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={spec.key}
                        onChange={(e) => updateSpecField(index, 'key', e.target.value)}
                        placeholder="Özellik Adı (örn: Bluetooth Versiyonu)"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                      />
                    </div>
                    <div className="flex-1">
                      <input
                        type="text"
                        value={spec.value}
                        onChange={(e) => updateSpecField(index, 'value', e.target.value)}
                        placeholder="Değer (örn: v5.3)"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeSpecField(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Sil"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold mb-4">Özel Etiketler</h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="featured"
                  checked={formData.featured}
                  onChange={handleChange}
                  className="w-4 h-4 accent-red-600"
                />
                <span className="text-sm">Öne Çıkan</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="bestseller"
                  checked={formData.bestseller}
                  onChange={handleChange}
                  className="w-4 h-4 accent-red-600"
                />
                <span className="text-sm">Çok Satan</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="new"
                  checked={formData.new}
                  onChange={handleChange}
                  className="w-4 h-4 accent-red-600"
                />
                <span className="text-sm">Yeni Ürün</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="weekly_deal"
                  checked={formData.weekly_deal}
                  onChange={handleChange}
                  className="w-4 h-4 accent-red-600"
                />
                <span className="text-sm">Haftanın Fırsatı</span>
              </label>
            </div>
          </div>

          <div className="flex gap-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 transition-colors font-semibold disabled:opacity-50"
            >
              {loading ? 'Ekleniyor...' : 'Ürünü Ekle'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/products')}
              className="px-8 py-3 border-2 border-gray-300 rounded-lg hover:border-red-600 hover:text-red-600 transition-colors font-semibold"
            >
              İptal
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AdminAddProduct;
